import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";

import { api } from "../api";
import AudioPlayer from "../components/AudioPlayer";
import ExerciseCard from "../components/ExerciseCard";
import VideoPlayer from "../components/VideoPlayer";
import VideoTaskCounter from "../components/VideoTaskCounter";
import type {
  Block,
  BlockComplete,
  BlockItem,
  LessonExerciseComplete,
} from "../types";
import { haptic, showConfirm } from "../tg";
import { collectVideoUrls, prefetchUrls } from "../utils/prefetch";

type Props = {
  lessonId: string;
  lessonTitle: string;
  blocks: Block[] | undefined;
  onBlocksLoaded: (blocks: Block[]) => void;
  currentBlockIndex: number | undefined;
  onCurrentBlockIndexChange: (idx: number) => void;
  onBlockCompleted: (blockId: string, lessonCompleted: boolean) => void;
  onLessonCompleted: () => void;
};

export default function LessonScreen({
  lessonId,
  lessonTitle,
  blocks: cachedBlocks,
  onBlocksLoaded,
  currentBlockIndex,
  onCurrentBlockIndexChange,
  onBlockCompleted,
  onLessonCompleted,
}: Props) {
  // Локальное зеркало blocks: позволяет делать оптимистичное обновление при complete
  // без ожидания пока App обновит cache.
  const [blocks, setBlocks] = useState<Block[] | null>(cachedBlocks ?? null);
  const [currentIdx, setCurrentIdx] = useState<number>(currentBlockIndex ?? 0);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsInitialIndex, setNeedsInitialIndex] = useState(
    currentBlockIndex === undefined,
  );
  // Все блоки уже пройдены при входе в урок → юзер пересматривает урок.
  // В этом режиме «Готово» по уже-пройденному блоку POST не отправляет (см. onNextClick).
  const [isReplaying, setIsReplaying] = useState(false);

  // Подтягиваем blocks. Если cachedBlocks есть — рендерим из них без спиннера, в фоне fetch.
  // App-кеш (onBlocksLoaded) обновляем всегда, даже если экран успели размонтировать —
  // иначе при быстром «вошёл-вышел-вошёл» в урок кеш не успеет заполниться и второй
  // заход покажет skeleton заново.
  useEffect(() => {
    let cancelled = false;
    api<Block[]>(`/lessons/${lessonId}/blocks`)
      .then((data) => {
        onBlocksLoaded(data);
        if (cancelled) return;
        setBlocks(data);
        // Первый заход в этот урок — перематываем до первого незавершённого.
        // Если все блоки уже пройдены (юзер возвращается в завершённый урок) — стартуем
        // с первого блока, чтобы можно было пересмотреть с начала, не тыкая «Назад» N раз.
        // Повторный возврат — сохраняем currentBlockIndex, который юзер уже видел.
        if (needsInitialIndex) {
          const firstOpen = data.findIndex((b) => !b.completed);
          const allDone = firstOpen === -1;
          const idx = allDone ? 0 : firstOpen;
          setCurrentIdx(idx);
          onCurrentBlockIndexChange(idx);
          setIsReplaying(allDone);
          setNeedsInitialIndex(false);
        }
      })
      .catch((e: Error) => {
        if (!cancelled && !cachedBlocks) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // Перезапрашиваем только при смене lessonId — на тот же урок повторных fetch не делаем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // Prefetch видео следующего блока — fire-and-forget. Срабатывает при смене блока
  // и сразу после загрузки blocks. Аудио не префетчим — оно мелкое, успевает дотянуться
  // когда пользователь до блока доходит. Дедупликация — внутри prefetchUrls.
  useEffect(() => {
    if (!blocks) return;
    const next = blocks[currentIdx + 1];
    if (!next) return;
    prefetchUrls(collectVideoUrls([next]));
  }, [blocks, currentIdx]);

  // Синхронизируем локальный currentIdx с App-state, чтобы при unmount/mount позиция сохранилась.
  const updateCurrentIdx = (next: number) => {
    setCurrentIdx(next);
    onCurrentBlockIndexChange(next);
  };

  const current = blocks ? blocks[currentIdx] : null;
  const isLast = blocks ? currentIdx === blocks.length - 1 : false;

  const onNextClick = async () => {
    if (!current || !blocks || completing) return;

    haptic.selectionChanged();

    // Если уже completed (перемотка по пройденным блокам) — просто двигаемся вперёд без запроса.
    if (current.completed) {
      if (isLast) {
        haptic.impact("light");
        onLessonCompleted();
      } else {
        updateCurrentIdx(currentIdx + 1);
      }
      return;
    }

    setCompleting(true);
    const blockId = current.id;

    // Оптимистично пометим блок completed.
    const optimisticBlocks = blocks.map((b, i) =>
      i === currentIdx ? { ...b, completed: true } : b,
    );
    setBlocks(optimisticBlocks);

    try {
      const res = await api<BlockComplete>(`/blocks/${blockId}/complete`, {
        method: "POST",
      });
      // Уведомляем App: обновляет blocks в cache + lesson.blocks_completed + sections.
      onBlockCompleted(blockId, res.lesson_completed);
      if (res.lesson_completed || isLast) {
        haptic.impact("light");
        onLessonCompleted();
        return;
      }
      updateCurrentIdx(currentIdx + 1);
    } catch (e) {
      // Откат.
      setBlocks(blocks);
      setError((e as Error).message);
      haptic("error");
    } finally {
      setCompleting(false);
    }
  };

  const onBackClick = () => {
    if (currentIdx === 0 || completing) return;
    haptic.selectionChanged();
    updateCurrentIdx(currentIdx - 1);
  };

  // Обновляем lesson_exercise_counts[item_index] локально (и в App-cache).
  const updateExerciseCount = (
    blockId: string,
    itemIndex: number,
    newCount: number,
  ) => {
    if (!blocks) return;
    const next = blocks.map((b) =>
      b.id === blockId
        ? {
            ...b,
            lesson_exercise_counts: {
              ...b.lesson_exercise_counts,
              [itemIndex]: newCount,
            },
          }
        : b,
    );
    setBlocks(next);
    onBlocksLoaded(next);
  };

  // Локальное обновление dz_taken на блоке после take/untake-optional.
  const updateBlockTaken = (blockId: string, taken: boolean) => {
    if (!blocks) return;
    const next = blocks.map((b) =>
      b.id === blockId ? { ...b, dz_taken: taken } : b,
    );
    setBlocks(next);
    onBlocksLoaded(next);
  };

  // Чекбокс «Дневные» на блоке. Required-блок снять нельзя (чекбокс disabled).
  // Снятие — с confirm, чтобы случайный тап не выкинул блок из ДЗ.
  const onBlockDzToggle = async (block: Block, checked: boolean) => {
    if (block.dz_required) return;
    if (!checked) {
      const ok = await showConfirm("Убрать блок из Дневных?");
      if (!ok) return;
    }
    haptic.selectionChanged();
    try {
      await api<void>(
        checked ? "/homework/take-optional" : "/homework/untake-optional",
        {
          method: "POST",
          body: JSON.stringify({ block_id: block.id }),
        },
      );
      updateBlockTaken(block.id, checked);
    } catch (e) {
      haptic("error");
      console.warn("block dz toggle failed:", (e as Error).message);
    }
  };

  const nextLabel = current
    ? current.button_caption || (isLast ? "Закончить" : "Далее →")
    : "Далее →";
  const isFirst = currentIdx === 0;

  return (
    <div className="screen screen-with-bottombar screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>{lessonTitle}</h1>
        {/* Резервируем строку под subtitle всегда — чтобы при появлении blocks
            header не «подрастал» и не толкал контент вниз. */}
        <p className="subtitle">
          {blocks
            ? `Блок ${currentIdx + 1} из ${blocks.length}${isReplaying ? " · повторение" : ""}`
            : "\u00A0"}
        </p>
      </header>

      {error && <div className="error-card">{error}</div>}

      {/* Skeleton того же layout что финальная карточка — никаких пустых пикселей
          сменяющихся на контент. Высоты подобраны под audio + title + текст. */}
      {!blocks && !error && (
        <div className="block-skeleton" aria-hidden="true">
          <div className="block-skeleton-line block-skeleton-line-audio" />
          <div className="block-skeleton-line block-skeleton-line-title" />
          <div className="block-skeleton-line" />
          <div className="block-skeleton-line block-skeleton-line-short" />
          <div className="block-skeleton-line block-skeleton-line-shorter" />
        </div>
      )}

      {current && (
        <article className="block-card">
          <div className="block-item-separated block-item-separated-first">
            <h2 className="block-title">{current.title}</h2>
            {/* Чекбокс «Дневные» — на уровне блока, виден только если есть exercise items. */}
            {current.items.some((it) => it.type === "exercise") && (
              <div className="block-dz-row">
                <label className="exercise-dz-checkbox">
                  <input
                    type="checkbox"
                    checked={current.dz_required || current.dz_taken}
                    disabled={current.dz_required}
                    onChange={(e) => onBlockDzToggle(current, e.target.checked)}
                  />
                  <span>Дневные</span>
                </label>
              </div>
            )}
          </div>
          {current.items.length === 0 && (
            <p className="block-empty">Содержимое блока пока пустое.</p>
          )}
          {current.items.map((item, i) => {
            // Заголовок блока уже отрендерен сверху отдельным блоком — items
            // нумеруются дальше с тем же separator-классом.
            // exercise/user_video_record рендерятся как карточки с собственной
            // рамкой — у них верхний разделитель лишний.
            const isCardType =
              item.type === "exercise" || item.type === "user_video_record";
            const itemClass = isCardType
              ? "block-item-separated block-item-card-type"
              : "block-item-separated";
            let inner: React.ReactNode = null;
            switch (item.type) {
              case "paragraph":
                inner = (
                  <div className="block-text">
                    <ReactMarkdown>{item.text}</ReactMarkdown>
                  </div>
                );
                break;
              case "audio":
                inner = (
                  <AudioPlayer
                    src={item.url}
                    title={item.title}
                    caption={item.caption ?? null}
                  />
                );
                break;
              case "video":
                inner = (
                  <VideoPlayer
                    url={item.url}
                    title={item.title}
                    width={item.width ?? null}
                    height={item.height ?? null}
                    caption={item.caption ?? null}
                    loop={item.is_gif === true}
                  />
                );
                break;
              case "image":
                inner = (
                  <figure className="block-image">
                    <img src={item.url} alt={item.caption ?? ""} />
                    {item.caption && (
                      <figcaption className="media-caption">{item.caption}</figcaption>
                    )}
                  </figure>
                );
                break;
              case "exercise":
                inner = (
                  <LessonExercise
                    item={item}
                    blockId={current.id}
                    itemIndex={i}
                    count={current.lesson_exercise_counts[i] ?? 0}
                    onCountChanged={(newCount) =>
                      updateExerciseCount(current.id, i, newCount)
                    }
                  />
                );
                break;
              case "user_video_record":
                // max_recordings === 0 — блок «выключен» админом, не рендерим вообще.
                // Лучше не отдавать пустой div, иначе появится пустой separator.
                if (item.max_recordings <= 0) {
                  return null;
                }
                inner = (
                  <VideoTaskCounter
                    blockId={current.id}
                    itemIndex={i}
                    title={item.title}
                    caption={item.caption}
                    maxCount={item.max_recordings}
                    initialCount={current.video_record_counts[i] ?? 0}
                  />
                );
                break;
            }
            return (
              <div key={i} className={itemClass}>
                {inner}
              </div>
            );
          })}
        </article>
      )}

      <div className="lesson-bottom-bar">
        <button
          type="button"
          className="lesson-nav-btn lesson-nav-btn-secondary"
          onClick={onBackClick}
          disabled={isFirst || completing}
        >
          ← Назад
        </button>
        <button
          type="button"
          className="lesson-nav-btn lesson-nav-btn-primary"
          onClick={onNextClick}
          disabled={!current || completing}
        >
          {completing ? "…" : nextLabel}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// LessonExercise — обёртка ExerciseCard для контекста урока.
// «Готово» → POST /v1/lesson/exercise/complete (накопительный счётчик).
// Чекбокс «Дневные» теперь живёт на уровне блока, не на упражнении.
// ----------------------------------------------------------------------------

type ExerciseData = Extract<BlockItem, { type: "exercise" }>;

type LessonExerciseProps = {
  item: ExerciseData;
  blockId: string;
  itemIndex: number;
  count: number;
  onCountChanged: (newCount: number) => void;
};

function LessonExercise({
  item,
  blockId,
  itemIndex,
  count,
  onCountChanged,
}: LessonExerciseProps) {
  const handleComplete = async () => {
    const res = await api<LessonExerciseComplete>("/lesson/exercise/complete", {
      method: "POST",
      body: JSON.stringify({ block_id: blockId, item_index: itemIndex }),
    });
    onCountChanged(res.count);
  };

  return (
    <ExerciseCard
      exercise={item}
      mode="lesson"
      count={count}
      onComplete={handleComplete}
    />
  );
}
